import type { CSSProperties } from 'react';
import { getBranding, type BrandingConfig, type LogoScaleConfig } from '../config/branding';
import { currentRuntime } from '../config/runtime';

type LogoTheme = 'light' | 'dark';

type AppLogoVariant = 'horizontalSplit' | 'horizontalLockup' | 'stacked' | 'symbol';

type CompanyLogoVariant = 'horizontal' | 'stacked' | 'symbol';

type LogoBaseSize = {
	width?: string;
	height?: string;
	maxWidth?: string;
	maxHeight?: string;
};

type LogoComputedSize = {
	base: LogoBaseSize;
	scale: number;
	style: CSSProperties;
};

const APP_LOGO_SIZE_FALLBACKS: Record<string, string[]> = {
	horizontalSplit: ['horizontalSplit', 'horizontalLockup', 'stacked', 'symbol'],
	horizontalLockup: ['horizontalLockup', 'horizontalSplit', 'stacked', 'symbol'],
	stacked: ['stacked', 'horizontalLockup', 'horizontalSplit', 'symbol'],
	symbol: ['symbol', 'stacked', 'horizontalLockup', 'horizontalSplit'],
};

const COMPANY_LOGO_SIZE_FALLBACKS: Record<string, string[]> = {
	horizontal: ['horizontal', 'stacked', 'symbol'],
	stacked: ['stacked', 'horizontal', 'symbol'],
	symbol: ['symbol', 'stacked', 'horizontal'],
};

const APP_LOGO_VARIANT_FALLBACKS: Record<AppLogoVariant, AppLogoVariant[]> = {
	horizontalSplit: ['horizontalSplit', 'horizontalLockup', 'stacked', 'symbol'],
	horizontalLockup: ['horizontalLockup', 'horizontalSplit', 'stacked', 'symbol'],
	stacked: ['stacked', 'horizontalLockup', 'horizontalSplit', 'symbol'],
	symbol: ['symbol', 'stacked', 'horizontalLockup', 'horizontalSplit'],
};

const COMPANY_LOGO_VARIANT_FALLBACKS: Record<CompanyLogoVariant, CompanyLogoVariant[]> = {
	horizontal: ['horizontal', 'stacked', 'symbol'],
	stacked: ['stacked', 'horizontal', 'symbol'],
	symbol: ['symbol', 'stacked', 'horizontal'],
};

const APP_LOGO_BASE_SIZES: Record<AppLogoVariant, LogoBaseSize> = {
	horizontalSplit: {
		height: 'clamp(4.25rem, 4.8vw, 5.1rem)',
		maxWidth: '100%',
	},
	horizontalLockup: {
		height: '4.8rem',
		maxWidth: '100%',
	},
	stacked: {
		height: '4.8rem',
		maxWidth: '100%',
	},
	symbol: {
		width: '2.5rem',
		height: '2.5rem',
	},
};

const COMPANY_LOGO_BASE_SIZES: Record<CompanyLogoVariant, LogoBaseSize> = {
	horizontal: {
		height: '64px',
		maxWidth: '100%',
	},
	stacked: {
		height: '64px',
		maxWidth: '100%',
	},
	symbol: {
		width: '80px',
		height: '80px',
	},
};

function resolveLogoSource(
	variants: Record<string, string | undefined> | undefined,
	orderedVariants: readonly string[],
	theme: LogoTheme,
) {
	if (!variants) return undefined;

	const suffix = theme === 'dark' ? 'Dark' : 'Light';

	for (const variant of orderedVariants) {
		const src = variants[`${variant}${suffix}`];
		if (src) return src;
	}

	return undefined;
}

function fallbackBrandLogo(brand: BrandingConfig) {
	return brand.logos.faviconSvg || brand.logos.favicon || '';
}

function resolveLogoScale<T extends string>(variants: Record<string, LogoScaleConfig | undefined> | undefined, orderedVariants: readonly T[]) {
	if (!variants) return undefined;

	for (const variant of orderedVariants) {
		const scale = variants[variant]?.scale;
		if (typeof scale === 'number' && Number.isFinite(scale)) return scale;
	}

	return undefined;
}

function scaleLength(length: string, scale: number) {
	return scale === 1 ? length : `calc(${length} * ${scale})`;
}

function logoSizeToStyle(base: LogoBaseSize, scale = 1): CSSProperties {
	const style: CSSProperties = {};
	if (base.width) style.width = scaleLength(base.width, scale);
	if (base.height) style.height = scaleLength(base.height, scale);
	if (base.maxWidth) style.maxWidth = scaleLength(base.maxWidth, scale);
	if (base.maxHeight) style.maxHeight = scaleLength(base.maxHeight, scale);
	return style;
}

function logoSizeModel(base: LogoBaseSize, scale = 1): LogoComputedSize {
	return {
		base,
		scale,
		style: logoSizeToStyle(base, scale),
	};
}

export function getAppLogoSrc(options: {
	brand?: BrandingConfig;
	theme?: LogoTheme;
	variant?: AppLogoVariant;
} = {}) {
	const brand = options.brand ?? getBranding(currentRuntime.brand.id);
	const theme = options.theme ?? 'light';
	const variant = options.variant ?? 'horizontalSplit';

	return (
		resolveLogoSource(brand.logos.app, APP_LOGO_VARIANT_FALLBACKS[variant], theme)
		|| resolveLogoSource(brand.logos.company, COMPANY_LOGO_VARIANT_FALLBACKS.symbol, theme)
		|| fallbackBrandLogo(brand)
	);
}

export function getAppLogoSizing(options: {
	brand?: BrandingConfig;
	variant?: AppLogoVariant;
} = {}) {
	const brand = options.brand ?? getBranding(currentRuntime.brand.id);
	const variant = options.variant ?? 'horizontalSplit';
	const scale = resolveLogoScale(brand.logos.appLogoSizing, APP_LOGO_SIZE_FALLBACKS[variant]) ?? 1;
	return logoSizeModel(APP_LOGO_BASE_SIZES[variant], scale);
}

export function getAppLogoSizeStyle(options: {
	brand?: BrandingConfig;
	variant?: AppLogoVariant;
} = {}) {
	return getAppLogoSizing(options).style;
}

export function getCompanyLogoSrc(options: {
	brand?: BrandingConfig;
	theme?: LogoTheme;
	variant?: CompanyLogoVariant;
} = {}) {
	const brand = options.brand ?? getBranding(currentRuntime.brand.id);
	const theme = options.theme ?? 'light';
	const variant = options.variant ?? 'horizontal';

	return (
		resolveLogoSource(brand.logos.company, COMPANY_LOGO_VARIANT_FALLBACKS[variant], theme)
		|| resolveLogoSource(brand.logos.app, APP_LOGO_VARIANT_FALLBACKS.symbol, theme)
		|| fallbackBrandLogo(brand)
	);
}

export function getCompanyLogoSizing(options: {
	brand?: BrandingConfig;
	variant?: CompanyLogoVariant;
} = {}) {
	const brand = options.brand ?? getBranding(currentRuntime.brand.id);
	const variant = options.variant ?? 'horizontal';
	const scale = resolveLogoScale(brand.logos.companyLogoSizing, COMPANY_LOGO_SIZE_FALLBACKS[variant]) ?? 1;
	return logoSizeModel(COMPANY_LOGO_BASE_SIZES[variant], scale);
}

export function getCompanyLogoSizeStyle(options: {
	brand?: BrandingConfig;
	variant?: CompanyLogoVariant;
} = {}) {
	return getCompanyLogoSizing(options).style;
}
